/**
 * Device identity (plan §4): a keypair persisted in IndexedDB whose public
 * JWK registers with the API and whose signatures back the Rago-Proof
 * header. Two implementations behind one seam, picked by what the origin
 * offers:
 * - WebCrypto (secure contexts: app.rago.tv, localhost): a non-extractable
 *   Ed25519 key, ES256 (P-256) fallback for pre-137 Chromium — the private
 *   key never leaves the browser.
 * - Software Ed25519 (plain-http LAN origins, where `crypto.subtle` is
 *   undefined — the server-hosted app): lib/softwareSigner, the raw key
 *   stored in the same record. Secure-context-ness is a property of the
 *   origin and so is the IndexedDB store, so one origin always resolves to
 *   the same implementation; a record written by one is never read by the
 *   other. The broker verifies both identically.
 */
import {
  b64uEncode,
  canonicalProofString,
  formatProofHeader,
  type ProofAlg,
  type PublicJwk,
} from "@rago/web-shared";
import { readClientIdCookie } from "./authCookie";
import { generateSoftwareKey, sha256Bytes, softwareSign, uuidV4 } from "./softwareSigner";
import { currentBrowser, currentBrowserVersion, currentPlatform, detectBrowserVersion } from "./userAgent";

const DB_NAME = "rago-device";
const STORE = "identity";
const KEY_ID = "device-v1";

/** What signProof needs from a key: the two primitives WebCrypto gates.
 * (`Uint8Array<ArrayBuffer>` is what TextEncoder yields and what
 * `crypto.subtle` accepts as a BufferSource.) */
export interface DeviceSigner {
  sign(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array>;
  sha256(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array>;
}

export interface DeviceIdentity {
  deviceId: string;
  alg: ProofAlg;
  publicJwk: PublicJwk;
  signer: DeviceSigner;
}

/** A record without `kind` predates the seam and is a WebCrypto key. */
type StoredIdentity =
  | { kind?: "webcrypto"; deviceId: string; alg: ProofAlg; publicJwk: PublicJwk; keyPair: CryptoKeyPair }
  | { kind: "software"; deviceId: string; alg: "EdDSA"; publicJwk: PublicJwk; priv: Uint8Array };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(db: IDBDatabase): Promise<StoredIdentity | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly").objectStore(STORE).get(KEY_ID);
    tx.onsuccess = () => resolve(tx.result as StoredIdentity | undefined);
    tx.onerror = () => reject(tx.error);
  });
}

function idbPut(db: IDBDatabase, value: StoredIdentity): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite").objectStore(STORE).put(value, KEY_ID);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** WebCrypto is present only in secure contexts; `crypto.subtle` itself is
 * undefined elsewhere (not merely failing), so this is a plain feature test. */
export function hasWebCrypto(): boolean {
  return typeof crypto !== "undefined" && typeof crypto.subtle?.generateKey === "function";
}

async function generateWebCryptoKeyPair(): Promise<{ alg: ProofAlg; keyPair: CryptoKeyPair }> {
  try {
    const keyPair = (await crypto.subtle.generateKey({ name: "Ed25519" }, false, [
      "sign",
      "verify",
    ])) as CryptoKeyPair;
    return { alg: "EdDSA", keyPair };
  } catch {
    const keyPair = await crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign", "verify"],
    );
    return { alg: "ES256", keyPair };
  }
}

async function exportPublicJwk(alg: ProofAlg, keyPair: CryptoKeyPair): Promise<PublicJwk> {
  const jwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
  if (alg === "EdDSA") {
    return { kty: "OKP", crv: "Ed25519", x: jwk.x! };
  }
  return { kty: "EC", crv: "P-256", x: jwk.x!, y: jwk.y! };
}

function webCryptoSigner(alg: ProofAlg, keyPair: CryptoKeyPair): DeviceSigner {
  return {
    async sign(data) {
      const sig =
        alg === "EdDSA"
          ? await crypto.subtle.sign({ name: "Ed25519" }, keyPair.privateKey, data)
          : await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, keyPair.privateKey, data);
      return new Uint8Array(sig);
    },
    async sha256(data) {
      return new Uint8Array(await crypto.subtle.digest("SHA-256", data));
    },
  };
}

function softwareSigner(priv: Uint8Array): DeviceSigner {
  return {
    async sign(data) {
      return softwareSign(priv, data);
    },
    async sha256(data) {
      return sha256Bytes(data);
    },
  };
}

function toIdentity(record: StoredIdentity): DeviceIdentity {
  if (record.kind === "software") {
    return {
      deviceId: record.deviceId,
      alg: "EdDSA",
      publicJwk: record.publicJwk,
      signer: softwareSigner(record.priv),
    };
  }
  return {
    deviceId: record.deviceId,
    alg: record.alg,
    publicJwk: record.publicJwk,
    signer: webCryptoSigner(record.alg, record.keyPair),
  };
}

/** A fresh record keeps the id the API last saw for this browser when the
 * rago_tv_client_id cookie survived an IndexedDB purge (Safari clears
 * script-writable storage, not server-set cookies) — the device row, its
 * session and the "This device" chip stay the same; only the key is new. */
async function createRecord(): Promise<StoredIdentity> {
  const deviceId = readClientIdCookie() ?? crypto.randomUUID?.() ?? uuidV4();
  if (hasWebCrypto()) {
    const { alg, keyPair } = await generateWebCryptoKeyPair();
    return { kind: "webcrypto", deviceId, alg, publicJwk: await exportPublicJwk(alg, keyPair), keyPair };
  }
  const { priv, publicJwk } = generateSoftwareKey();
  return { kind: "software", deviceId, alg: "EdDSA", publicJwk, priv };
}

let cached: DeviceIdentity | null = null;

/** Loads (or creates on first run) this browser's device identity. */
export async function getDeviceIdentity(): Promise<DeviceIdentity> {
  if (cached) return cached;
  const db = await openDb();
  const existing = await idbGet(db);
  if (existing) {
    cached = toIdentity(existing);
    db.close();
    return cached;
  }
  const record = await createRecord();
  await idbPut(db, record);
  db.close();
  cached = toIdentity(record);
  return cached;
}

/** Wipes the identity (used when the server no longer recognizes the device). */
export async function resetDeviceIdentity(): Promise<void> {
  cached = null;
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

/** Signs a Rago-Proof header for the given request. */
export async function signProof(
  identity: DeviceIdentity,
  method: string,
  pathWithQuery: string,
  rawBody: string,
): Promise<string> {
  const ts = Math.floor(Date.now() / 1000);
  const nonceBytes = crypto.getRandomValues(new Uint8Array(12));
  const nonce = b64uEncode(nonceBytes);
  const bodyHash = await identity.signer.sha256(new TextEncoder().encode(rawBody));
  const canonical = canonicalProofString({
    method,
    pathWithQuery,
    ts,
    nonce,
    bodySha256B64u: b64uEncode(bodyHash),
  });
  const sig = await identity.signer.sign(new TextEncoder().encode(canonical));
  return formatProofHeader({
    did: identity.deviceId,
    alg: identity.alg,
    ts,
    nonce,
    sig: b64uEncode(sig),
  });
}

/**
 * The device registry's view of this browser (the `device` of sign-in and
 * sign-up, the pin body): "Rago Web" on the platform, named after the
 * browser and versioned by the browser — Chrome's full version comes from
 * the client-hints API (its UA string is frozen at x.0.0.0), the rest from
 * the UA token. Security › Sessions shows all of it.
 */
export async function deviceInfoPayload(identity: DeviceIdentity) {
  const platform = currentPlatform();
  const version = await currentBrowserVersion();
  return {
    deviceId: identity.deviceId,
    publicKey: identity.publicJwk,
    keyAlg: identity.alg,
    product: "Rago Web",
    platform,
    name: `${currentBrowser()} on ${platform}`,
    ...(version ? { version } : {}),
  };
}

/**
 * Player identity for server timeline reports — the dashboard's "player"
 * line. Display-oriented, unlike the cloud registration payload above, and
 * synchronous, so the version is the UA token (Chrome reports x.0.0.0).
 */
export function playerInfoPayload(): {
  product: string;
  name: string;
  platform: string;
  version?: string;
} {
  const platform = currentPlatform();
  const version = detectBrowserVersion(navigator.userAgent);
  return {
    product: "Rago Web",
    platform,
    name: `${currentBrowser()} on ${platform}`,
    ...(version ? { version } : {}),
  };
}
